import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { PlusIcon, TrashIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { newsroomApi, NewsSource } from '@/services/newsroomApi';

const NewsSourcesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ providerType: 'rss' as 'rss' | 'wire-api', name: '', outlet: '', language: 'en' as 'en' | 'ta', feedUrl: '' });

  const { data: sources = [], isLoading } = useQuery({
    queryKey: ['newsroom-sources'],
    queryFn: () => newsroomApi.getSources(),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['newsroom-sources'] });

  const createMutation = useMutation({
    mutationFn: () => newsroomApi.createSource(form),
    onSuccess: () => {
      toast.success('Source added');
      setShowForm(false);
      setForm({ providerType: 'rss', name: '', outlet: '', language: 'en', feedUrl: '' });
      invalidate();
    },
    onError: (error: any) => toast.error(error?.response?.data?.message || 'Failed to add source'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => newsroomApi.deleteSource(id),
    onSuccess: () => {
      toast.success('Source removed');
      invalidate();
    },
  });

  const pollMutation = useMutation({
    mutationFn: (id: string) => newsroomApi.pollSourceNow(id),
    onSuccess: (result) => {
      toast.success(`Fetched ${result.created} new article(s)`);
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['newsroom-articles'] });
    },
    onError: (error: any) => toast.error(error?.response?.data?.message || 'Poll failed'),
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) => newsroomApi.updateSource(id, { isActive }),
    onSuccess: invalidate,
  });

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Newsroom Sources</h1>
        <button
          onClick={() => setShowForm((v) => !v)}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
        >
          <PlusIcon className="h-4 w-4" />
          Add source
        </button>
      </div>

      {showForm && (
        <div className="mb-6 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input
              placeholder="Name (e.g. The Hindu — National)"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white p-2"
            />
            <input
              placeholder="Outlet key (e.g. the-hindu)"
              value={form.outlet}
              onChange={(e) => setForm((f) => ({ ...f, outlet: e.target.value }))}
              className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white p-2"
            />
            <select
              value={form.providerType}
              onChange={(e) => setForm((f) => ({ ...f, providerType: e.target.value as 'rss' | 'wire-api' }))}
              className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white p-2"
            >
              <option value="rss">RSS feed</option>
              <option value="wire-api">Wire API (not yet available)</option>
            </select>
            <select
              value={form.language}
              onChange={(e) => setForm((f) => ({ ...f, language: e.target.value as 'en' | 'ta' }))}
              className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white p-2"
            >
              <option value="en">English</option>
              <option value="ta">Tamil</option>
            </select>
          </div>
          {form.providerType === 'rss' && (
            <input
              placeholder="RSS feed URL"
              value={form.feedUrl}
              onChange={(e) => setForm((f) => ({ ...f, feedUrl: e.target.value }))}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white p-2"
            />
          )}
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowForm(false)} className="px-3 py-2 text-sm text-gray-600 dark:text-gray-300">
              Cancel
            </button>
            <button
              onClick={() => createMutation.mutate()}
              disabled={!form.name || !form.outlet || createMutation.isLoading}
              className="px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
            >
              {createMutation.isLoading ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="space-y-2">
          {sources.map((source: NewsSource) => (
            <div
              key={source._id}
              className="flex items-center justify-between rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3"
            >
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">{source.name}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {source.outlet} · {source.language.toUpperCase()} · {source.providerType}
                  {source.lastPollStatus && ` · last poll: ${source.lastPollStatus}`}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                  <input
                    type="checkbox"
                    checked={source.isActive}
                    onChange={(e) => toggleActiveMutation.mutate({ id: source._id, isActive: e.target.checked })}
                  />
                  Active
                </label>
                <button
                  onClick={() => pollMutation.mutate(source._id)}
                  disabled={pollMutation.isLoading}
                  title="Poll now"
                  className="text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                >
                  <ArrowPathIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => deleteMutation.mutate(source._id)}
                  title="Remove"
                  className="text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
          {sources.length === 0 && (
            <p className="text-center py-10 text-sm text-gray-500 dark:text-gray-400">No sources configured yet.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default NewsSourcesPage;
